import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import Button from "./Button"

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  primaryAction = null,
  primaryActionLabel = "Continue",
  primaryButtonVariant = "addbutton",
  secondaryAction = null,
  secondaryActionLabel = "Back",
  secondaryButtonVariant = "addbutton",
  tertiaryAction = null,
  tertiaryActionLabel = "Cancel",
  tertiaryButtonVariant = "addbutton",
  showCloseButton = false,
  size = "xl", // sm, md, lg, xl
}) {
  const modalRef = useRef(null)

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEsc)
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEsc)
      document.body.style.overflow = "auto"
    }
  }, [isOpen, onClose])

  // Handle click outside to close modal
  const handleOutsideClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose()
    }
  }

  // Size classes
  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    max: "max-w-2xl"
  }

  if (!isOpen) return null

  // Determine the footer layout based on how many buttons we have
  const getFooterLayout = () => {
    const buttonCount = [primaryAction, secondaryAction, tertiaryAction].filter(Boolean).length

    if (buttonCount === 1) return "justify-center"
    if (buttonCount === 2) return "justify-between gap-4"
    if (buttonCount === 3) return "justify-between gap-2"

    return "justify-center"
  }

  // Use createPortal to render the modal at the end of the document body
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
      onClick={handleOutsideClick}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-xl shadow-lg w-full ${
          sizeClasses[size] || sizeClasses.md
        } overflow-hidden border-4 border-border-modal`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 pb-3">
          <h2 className="text-xl font-semibold text-center">{title}</h2>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              &times;
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="px-6 py-3 text-gray-600">{children}</div>

        {/* Modal Footer */}
        <div className={`p-8 pt-3 flex flex-wrap ${getFooterLayout()}`}>
          {tertiaryAction && (
            <Button onClick={tertiaryAction} variant={tertiaryButtonVariant} className="text-black px-8 py-2">
              {tertiaryActionLabel}
            </Button>
          )}
          {secondaryAction && (
            <Button
              onClick={secondaryAction || onClose}
              variant={secondaryButtonVariant}
              className="text-black px-8 py-2"
            >
              {secondaryActionLabel}
            </Button>
          )}
          {primaryAction && (
            <Button onClick={primaryAction} variant={primaryButtonVariant} className="text-black px-8 py-2">
              {primaryActionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
